import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { submitSurvey } from '@/service/api';
import dissatisfied from '@/assets/dissatisfied.svg';
import neutral from '@/assets/neutral.svg';
import friendly from '@/assets/friendly.svg';
import './index.less';
import { setIsOpen } from '@/store/slices/chatSlice';
import { addMessage, setSurveyEnabled, Message } from '@/store/slices/chatSlice';

const Survey: React.FC = () => {
    const currentSessionId = useSelector((state: RootState) => state.chat.currentSessionId);
    const curTheme = useSelector((state: RootState) => state.theme);
    const [ rating, setRating ] = useState<number>(10);
    const [ firstAttempt, setFirstAttempt ] = useState<'yes' | 'no' | null>(null);
    const [ comment, setComment ] = useState<string>('');

    const message10 = 'Thank you for the excellent rating! We\'re delighted to have been able to assist you.​\n\n It was a pleasure chatting with you. I will close this chat for now. Please feel free to reach out again if you need support in the future.​';

    const message0 = 'We apologize for not meeting your expectations. Your feedback is valuable to us, and we will work on making improvements to enhance your experience.​​\n\nIt was a pleasure chatting with you. I will close this chat for now. Feel free to reach out again if you need support in the future.';

    const satisfactionText = useMemo(() => {
        if (rating <= 4) return 'Dissatisfied';
        if (rating === 5) return 'Somewhat satisfied';
        return 'Satisfied';
    }, [ rating ]);

    // 根据分值选择不同的本地 SVG 图片
    const emojiSrc = useMemo(() => {
        if (rating <= 4) return dissatisfied;
        if (rating === 5) return neutral;
        return friendly;
    }, [ rating ]);

    const [ submitting, setSubmitting ] = useState(false);
    const dispatch = useDispatch();
    const isSurveyEnabled = useSelector((state: RootState) => state.chat.isSurveyEnabled);

    const handleSubmit = async () => {
        // 校验第1题与第2题
        if (rating === null || rating === undefined) {
            message.warning('Please select a rating.');
            return;
        }
        if (!firstAttempt) {
            message.warning('Please choose Yes or No.');
            return;
        }

        setSubmitting(true);
        const payload = {
            rating,
            resolvedOnFirstAttempt: firstAttempt === 'yes',
            comment: comment?.trim() ? comment.trim() : undefined,
            session_id: currentSessionId || 'unknown'
        };
        submitSurvey(payload)
            .then((res) => {
                console.log('submitSurvey response:', res);
            })
            .catch((error) => {
                console.log('submitSurvey error:', error);
            });
        message.success('Survey submitted successfully.');
        dispatch(setIsOpen(false));
        dispatch(setSurveyEnabled(false));
        const botMessage: Message = {
            id: `assistant-${Date.now()}`,
            content: rating > 7 ? message10 : message0,
            role: 'assistant',
            timestamp: Date.now()
        };
        dispatch(addMessage(botMessage));
        setSubmitting(false);
    };

    return (
        <div className={`survey-card ${curTheme}-survey-card`} role="form" aria-label="Rate AIR service">
            <div className="survey-header">Rate AIR service</div>

            <div className="survey-section">
                <div className="survey-q-title">
                    <span className="q-index">*1.</span>
                    Overall, how satisfied are you with the support you received from us?
                </div>
                <div className="survey-rate" role="radiogroup" aria-label="Satisfaction rating 0 to 10">
                    {Array.from({ length: 11 }).map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            className={`rate-item color-${i} ${i === rating ? 'active' : ''}`}
                            role="radio"
                            aria-checked={i === rating}
                            onClick={() => setRating(i)}
                        >
                            <span className="rate-num">{i}</span>
                        </button>
                    ))}
                </div>
                <div className="survey-rate-desc">
                    <span className="emoji" aria-hidden>
                        <img src={emojiSrc} alt="" width={16} height={16} />
                    </span>
                    <span className="text">{satisfactionText}</span>
                </div>
            </div>

            <div className="survey-section">
                <div className="survey-q-title">
                    <span className="q-index">*2.</span>
                    Was your question answered or issue resolved on the first attempt?
                </div>
                <div className="survey-yn radio-group" role="radiogroup" aria-label="Resolved on first attempt">
                    <label className={`radio ${firstAttempt === 'yes' ? 'checked' : ''}`}>
                        <input
                            type="radio"
                            name="firstAttempt"
                            checked={firstAttempt === 'yes'}
                            onChange={() => setFirstAttempt('yes')}
                        />
                        <span className="radio-label">Yes</span>
                    </label>
                    <label className={`radio ${firstAttempt === 'no' ? 'checked' : ''}`}>
                        <input
                            type="radio"
                            name="firstAttempt"
                            checked={firstAttempt === 'no'}
                            onChange={() => setFirstAttempt('no')}
                        />
                        <span className="radio-label">No</span>
                    </label>
                </div>
            </div>

            <div className="survey-section">
                <div className="survey-q-title">
                    <span className="q-index">3.</span>
                    Is there anything else we could have done to improve your experience?
                </div>
                <div className="survey-textarea">
                    <textarea
                        id="survey-comment"
                        aria-label="Additional comments and suggestions"
                        placeholder="Free comments & suggestions"
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        disabled={submitting || !isSurveyEnabled}
                    />
                </div>
            </div>

            <div className="survey-actions">
                <button className="survey-submit" type="button" disabled={submitting || !isSurveyEnabled} onClick={handleSubmit}>Send</button>
            </div>
        </div>
    );
};

export default Survey;


